/**
 * Compact date selector for a lottery bet row.
 *
 * Two modes (lit indicators show which mode is active):
 *   1. Single date  — one <input type="date"> picker (default today).
 *   2. Multi-day    — start date + N consecutive days (1..7).
 *
 * Returns an array of ISO YYYY-MM-DD strings via onChange. When no
 * customization happens, returns [today] so the existing backend
 * default behavior is preserved.
 *
 * Big 3 supports this same picker — backend now accepts a `dates` list.
 */
import { useMemo, useEffect } from 'react';
import { Calendar } from 'lucide-react';

// "YYYY-MM-DD" in America/Chicago (the casino's operational time zone)
const ctToday = () => {
    try {
        // Get CT date parts via toLocaleString
        const fmt = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'America/Chicago',
            year: 'numeric', month: '2-digit', day: '2-digit',
        });
        return fmt.format(new Date()); // en-CA → "YYYY-MM-DD"
    } catch {
        return new Date().toISOString().slice(0, 10);
    }
};

const addDays = (isoDate, n) => {
    // Compute next-day strings without timezone drift
    const [y, m, d] = isoDate.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + n);
    return dt.toISOString().slice(0, 10);
};

/**
 * Props:
 *   dates: string[]   — current selection (array of YYYY-MM-DD strings)
 *   onChange(dates)   — fires whenever the selection changes
 *   testIdPrefix: string  — optional prefix for data-testid hooks
 */
const BetDateSelector = ({ dates, onChange, testIdPrefix = 'date-selector' }) => {
    const today = useMemo(() => ctToday(), []);
    // Mode is derived from the current selection length
    const isMulti = (dates?.length || 0) > 1;
    const startDate = dates?.[0] || today;
    const dayCount = dates?.length || 1;

    // Seed default on mount if no dates passed
    useEffect(() => {
        if (!dates || dates.length === 0) onChange([today]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setMode = (multi) => {
        if (multi) {
            // Switching to multi — keep current start, default to 3 days
            const start = startDate || today;
            onChange([start, addDays(start, 1), addDays(start, 2)]);
        } else {
            onChange([startDate || today]);
        }
    };

    const setStart = (newStart) => {
        if (!newStart) return;
        if (isMulti) {
            const span = Array.from({ length: dayCount }, (_, i) => addDays(newStart, i));
            onChange(span);
        } else {
            onChange([newStart]);
        }
    };

    const setDayCount = (n) => {
        const span = Array.from({ length: n }, (_, i) => addDays(startDate, i));
        onChange(span);
    };

    const isToday = startDate === today && dayCount === 1;

    return (
        <div className="space-y-1.5" data-testid={`${testIdPrefix}-root`}>
            <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Draw date{isMulti ? 's' : ''}
                </span>
                <div className="flex bg-[#0a0a0a] border border-white/10 rounded p-0.5 text-[9px] font-bold">
                    <button
                        type="button"
                        onClick={() => setMode(false)}
                        className={`px-1.5 py-0.5 rounded transition ${!isMulti ? 'bg-[#7C3AED] text-white' : 'text-[#A0AAB2]'}`}
                        data-testid={`${testIdPrefix}-mode-single`}
                    >
                        Single
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode(true)}
                        className={`px-1.5 py-0.5 rounded transition ${isMulti ? 'bg-[#7C3AED] text-white' : 'text-[#A0AAB2]'}`}
                        data-testid={`${testIdPrefix}-mode-multi`}
                    >
                        Multi-day
                    </button>
                </div>
            </div>

            <div className="flex items-center gap-1.5 flex-wrap">
                <input
                    type="date"
                    value={startDate}
                    min={today}
                    onChange={(e) => setStart(e.target.value)}
                    className="h-7 bg-[#0a0a0a] border border-white/15 rounded text-white text-xs px-1.5"
                    data-testid={`${testIdPrefix}-start`}
                />
                {isMulti && (
                    <>
                        <span className="text-[10px] text-[#A0AAB2]">for</span>
                        <select
                            value={dayCount}
                            onChange={(e) => setDayCount(parseInt(e.target.value, 10) || 1)}
                            className="h-7 bg-[#0a0a0a] border border-white/15 rounded text-white text-xs px-1.5"
                            data-testid={`${testIdPrefix}-days`}
                        >
                            {[2, 3, 4, 5, 6, 7].map(n => (
                                <option key={n} value={n}>{`${n} days`}</option>
                            ))}
                        </select>
                    </>
                )}
                {isToday && (
                    <span className="text-[9px] text-[#39FF14] font-bold">TODAY</span>
                )}
            </div>

            {isMulti && dayCount > 1 && (
                <p className="text-[9px] text-[#A0AAB2]">
                    Playing {dayCount} consecutive draws · {startDate} → {addDays(startDate, dayCount - 1)}
                </p>
            )}
        </div>
    );
};

export default BetDateSelector;
